import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import useElementSize from '../../hooks/useElementSize';
import { useCategoryUrlGenerator, useFormatUrlGenerator } from '../../hooks/useUrlGenerator';

// Add missing type import or definition for Background
import Blocks from '../blocks/Blocks';
import PillButton from '../buttons/PillButton';
import Broadcasts from '../partials/Broadcasts';
import Credits from '../partials/Credits';
import Date from '../partials/Date';
import Sponsor from '../partials/Sponsor';
import Surtitle, { SurtitleValue } from '../partials/Surtitle';
import DocumentTheme from '../themes/DocumentTheme';
import Body from '../typography/Body';
import DocumentMetadata from '../typography/DocumentMetadata';
import HeaderTitle from '../typography/HeaderTitle';
import SurtitleTypography from '../typography/Surtitle';

import styles from '../../styles/documents/video-metadata.module.css';

// Adjust the path as needed

export interface VideoMetadataProps extends Partial<Omit<VideoLikeDocument, 'surtitle'>> {
    surtitle?: SurtitleValue | null;
    placeholder?: boolean;
    withoutImage?: boolean;
    className?: string | null;
    imageClassName?: string | null;
}

function VideoMetadata({
    id = null,
    type = null,
    title = null,
    surtitle = null,
    subtitle = null,
    metadata = null,
    theme = null,
    placeholder = false,
    blocks = null,
    className = null,
}: VideoMetadataProps) {
    const url = useUrlGenerator();
    const formatUrl = useFormatUrlGenerator();
    const categoryUrl = useCategoryUrlGenerator();

    const {
        brands,
        formats,
        categories,
        broadcasts = null,
        credits = null,
        sponsors = null,
        publishedAt = null,
    } = metadata || {};
    const { name: themeName = null } = theme || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};
    const [sponsor = null] = sponsors || [];

    const pills = [
        ...(formats || []).map(({ label, slug }) => ({
            label,
            href: formatUrl({ slug }),
        })),
        ...(categories || []).map(({ label, slug }) => ({
            label,
            href: categoryUrl({ slug }),
        })),
    ];

    const [descriptionHasScroll, setDescriptionHasScroll] = useState(false);
    const [descriptionOpened, setDescriptionOpened] = useState(false);
    const { ref: descriptionRef, width, height } = useElementSize();
    useEffect(() => {
        const { current: descriptionElement } = descriptionRef;
        if (descriptionElement !== null) {
            const hasScroll =
                descriptionElement.scrollHeight > height &&
                descriptionElement.scrollHeight - height >= 10;
            setDescriptionHasScroll(hasScroll);
        }
    }, [width, height]);
    const onClickViewMore = () => {
        setDescriptionOpened((opened) => !opened);
    };

    return (
        <DocumentTheme
            id={`video-${id}`}
            {...theme}
            containerClassName={styles.container}
            className={classNames([
                styles.container,
                styles[brandHandle],
                {
                    [styles.placeholder]: placeholder,
                },
                className,
            ])}
            data-brand={brandHandle}
            data-theme={themeName}
        >
            <div
                className={classNames([
                    styles.content,
                    {
                        [styles.descriptionOpened]: descriptionOpened,
                        [styles.descriptionHasScroll]: descriptionHasScroll,
                    },
                    styles.document,
                ])}
            >
                <div className={styles.inner}>
                    {surtitle !== null ? (
                        <Surtitle surtitle={surtitle} className={styles.surtitle} />
                    ) : null}
                    <HeaderTitle className={styles.title}>{title}</HeaderTitle>
                    {publishedAt !== null ? (
                        <DocumentMetadata className={classNames([styles.date])}>
                            <Date date={publishedAt} />
                        </DocumentMetadata>
                    ) : null}
                    {subtitle !== null ? (
                        <SurtitleTypography className={styles.subtitle}>
                            {subtitle}
                        </SurtitleTypography>
                    ) : null}

                    {sponsor !== null ? (
                        <Sponsor
                            {...sponsor}
                            withImage
                            className={styles.sponsor}
                            imageClassName={styles.sponsorImage}
                        />
                    ) : null}
                    {broadcasts !== null && broadcasts.length > 0 ? (
                        <Broadcasts
                            broadcasts={broadcasts}
                            type={type}
                            className={styles.broadcasts}
                        />
                    ) : null}
                    {blocks !== null ? (
                        <div className={styles.descriptionContainer}>
                            <div className={styles.description} ref={descriptionRef}>
                                <Body className={styles.body}>
                                    <Blocks
                                        blocks={blocks}
                                        withoutMaxWidth
                                        className={styles.blocks}
                                    />
                                </Body>
                            </div>
                            <PillButton
                                compact
                                className={styles.viewMore}
                                onClick={onClickViewMore}
                            >
                                {descriptionOpened ? 'Voir moins' : 'Voir plus'}
                            </PillButton>
                        </div>
                    ) : null}
                    <div className={styles.footer}>
                        {pills.length > 0 ? (
                            <div className={styles.pills}>
                                {pills.map(({ label, href }) => (
                                    <PillButton key={`pill-${href}`} href={href} bordered compact>
                                        {label}
                                    </PillButton>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    {credits !== null && credits.length > 0 ? (
                        <Credits credits={credits} className={styles.credits} />
                    ) : null}
                </div>
            </div>
        </DocumentTheme>
    );
}

export default VideoMetadata;
